import React from 'react';
import Icon from '../Icon/Icon';
import Box from '../Box/Box';

import TInpageIcon from './types/TInpageIcon';
import TInpageBody from './types/TInpageBody';
import TInpageBase from './types/TInpageBase';

const name = 'Inpage';

const InpageIcon = ({ icon, signal }: TInpageIcon) => {
  if (!icon) return null;
  return (
    <Box as="div" parent={name} name="icon">
      <Icon name={icon} color={signal} />
    </Box>
  );
};

function isOnlyDescriptionInChildren(title, description) {
  return !(title || description);
}

const InpageBody = ({ title, titleHeadingElement = 'p', description, children }: TInpageBody) => (
  <Box as="fieldset" parent={name} name="body" data-only-description={isOnlyDescriptionInChildren(title, description)}>
    {title && (
      <Box as={titleHeadingElement} parent={name} name="title">
        {title}
      </Box>
    )}
    {description && <p>{description}</p>}
    {children}
  </Box>
);

function handleCloseKeyDown(onClose: () => void) {
  return (event: React.KeyboardEvent) => {
    const isSpaceOrEnter = (key: string) => ['Enter', ' '].includes(key);
    if (isSpaceOrEnter(event.key)) {
      event.preventDefault();
      onClose();
    }
  };
}

type TInpageClose = {
  onClose?: {
    action?: () => void;
    label: string;
  };
};

const InpageClose = ({ onClose }: TInpageClose) => {
  if (!(onClose && onClose?.action)) return null;
  return (
    <Box
      as="div"
      parent={name}
      name="close"
      onClick={onClose.action}
      onKeyDown={handleCloseKeyDown(onClose.action)}
      role="button"
      tabIndex={0}
      aria-label={onClose.label ?? 'close'}
    >
      <Icon name="close" />
    </Box>
  );
};

function getAriaLive(signal: string) {
  return signal !== 'info' ? 'polite' : 'off';
}

const InpageBase = ({ signal, icon, title, description, children, onClose, ...rest }: TInpageBase) => (
  <Box as="div" {...rest} name={name} modifiers={signal} aria-live={getAriaLive(signal)}>
    <InpageIcon signal={signal} icon={icon} />
    <InpageBody title={title} description={description}>
      {children}
    </InpageBody>
    <InpageClose onClose={onClose} />
  </Box>
);

export default InpageBase;
